import { useMemo } from 'react';
import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import ControlledTable from 'components/shared/table/ControlledTable';
import { useProfile } from 'context/profile';
import usePagination from 'hooks/usePagination';
import { simpleDate } from 'lib/date';
import { Paginated } from 'models/Paginated';
import { PreferredProviderListRow } from 'models/PreferredProviderList';
import { PERMISSIONS } from 'models/Profile';
import { usePreferredProvidersActions, usePreferredProvidersStore } from 'stores/preferredProvidersStore';
import { colors } from 'styles/theme/colors';
import PencilIcon from 'svg/PencilIcon';

type Props = {
  data: Paginated<PreferredProviderListRow> | undefined;
  isLoading: boolean;
  pagination: ReturnType<typeof usePagination>;
  onOpen: (row: PreferredProviderListRow) => void;
};

const columnHelper = createColumnHelper<PreferredProviderListRow>();

const PreferredProvidersTable = ({ data, isLoading, pagination, onOpen }: Props) => {
  const { profile } = useProfile();
  const filters = usePreferredProvidersStore((state) => state.filters);
  const sort = usePreferredProvidersStore((state) => state.sort);
  const { setSort } = usePreferredProvidersActions();
  const canEdit = profile.has(PERMISSIONS.preferredProviderEdit);

  const columns = useMemo(
    () => [
      columnHelper.accessor('locationName', {
        header: 'Location',
        id: 'locationName',
        meta: { style: { fontWeight: 500 } },
      }),
      columnHelper.accessor((row) => row.preferredProviders.length, {
        id: 'count',
        header: 'Preferred Providers',
        enableSorting: false,
        cell: (info) => {
          const label = `${info.getValue()} provider${info.getValue() === 1 ? '' : 's'}`;
          if (!info.getValue()) return label;

          return <LinkButton onClick={() => onOpen(info.row.original)}>{label}</LinkButton>;
        },
      }),
      columnHelper.accessor('updatedAt', {
        header: 'Last Updated',
        id: 'updatedAt',
        cell: (info) =>
          info.getValue() ? (
            `${simpleDate(info.getValue())}${info.row.original.updatedByName ? ` by ${info.row.original.updatedByName}` : ''}`
          ) : (
            <>&ndash;</>
          ),
      }),
      columnHelper.display({
        id: 'edit',
        header: '',
        meta: { style: { justifyContent: 'flex-end' } },
        cell: (info) =>
          canEdit && (
            <EditButton onClick={() => onOpen(info.row.original)}>
              <PencilIcon width={12} height={12} color={colors.blue100} /> Edit
            </EditButton>
          ),
      }),
    ],
    [canEdit, onOpen]
  );

  return (
    <ControlledTable
      data={data?.data ?? []}
      filters={filters}
      columns={columns}
      loading={isLoading}
      pageCount={data?.meta.totalPages ?? 1}
      pagination={pagination}
      sorting={{ sortBy: sort, setSortBy: setSort }}
      // Every header click stays on a direction rather than cycling back to no sort, like the other
      // Configuration pages.
      enableSortingRemoval={false}
    />
  );
};

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  color: ${colors.blue100};
  cursor: pointer;
  text-decoration: underline;
`;

const EditButton = styled(LinkButton)`
  text-decoration: none;
`;

export default PreferredProvidersTable;
